import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  LOCALE_STORAGE_KEY,
  browserLanguages,
  loadLocalePreference,
  resolveLocalePreference,
  type AppLocale,
  type LocalePreference,
} from './localePreference'

type LocaleContextValue = {
  preference: LocalePreference
  locale: AppLocale
  setPreference: (value: LocalePreference) => void
  t: (turkish: string, english: string) => string
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<LocalePreference>(() => loadLocalePreference())
  const [locale, setLocale] = useState<AppLocale>(() => resolveLocalePreference(loadLocalePreference(), browserLanguages()))

  useEffect(() => {
    const apply = () => {
      const next = resolveLocalePreference(preference, browserLanguages())
      setLocale(next)
      document.documentElement.lang = next
    }
    apply()
    if (preference !== 'auto') return
    window.addEventListener('languagechange', apply)
    return () => window.removeEventListener('languagechange', apply)
  }, [preference])

  const setPreference = (next: LocalePreference) => {
    setPreferenceState(next)
    try { localStorage.setItem(LOCALE_STORAGE_KEY, next) } catch { /* storage kullanılamaz olabilir */ }
  }

  const value = useMemo<LocaleContextValue>(() => ({
    preference,
    locale,
    setPreference,
    t: (turkish, english) => locale === 'tr' ? turkish : english,
  }), [preference, locale])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const value = useContext(LocaleContext)
  if (!value) throw new Error('useLocale, LocaleProvider içinde kullanılmalı')
  return value
}
